import { useState, useEffect, createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";

// Actions
import { restoreMapSavedState } from "../../../../actions/map-view/SaveMapStateActions";
import { removeMediaSite } from "../../../../actions/map-view/media-sites/MediaSitesActions";

// Constants and Utils
import {
  useGetCampaignCustomTgHeatMap,
  useResetMapState,
} from "../../../../mavin/utils/hooks/HooksUtil";
import {
  CampaignMapViewId,
  MapView,
} from "../../../../constants/GeneralConstants";
import { MapInfoBox } from "../../../../pages/map-view/boundaries/BoundariesUtil";

// Components
import LLMap from "../../../../components/map/leaflet-map/LLMap";
import TargetGroupHeatmap from "../../../../components/map/target-group-heatmap/TargetGroupHeatmap";
import ConfirmationForm from "../../../../components/confirmation-form/ConfirmationForm";
import MapZoomIndicator from "../../../../components/map/map-zoom-indicator/MapZoomIndicator";

// Pages and Sections
import SiteMarkers from "../../../../pages/map-view/media-sites/SiteMarkers";
import DataLayerMarkers from "../../../../pages/map-view/data-layers/DataLayerMarkers";
import BoundaryMarkers from "../../../../pages/map-view/boundaries/BoundaryMarkers";
import SecLayer from "../../../../pages/map-view/sec/SecLayer";
import PoiMarkers from "../../../../components/map/markers/PoiMarkers";
import MapTabsSection from "./MapTabsSection";
import RoadStretchMarkers from "../../../../pages/map-view/road-stretches/RoadStretchMarkers";
import PincodeRankingMarkers from "../../../../pages/map-view/pincode-ranking/PincodeRankingMarkers";
import MapControlsSection from "../../../../pages/map-view/MapControlsSection";
import {
  CampaignMapViewHeader,
  MapViewLoader,
} from "../../../../pages/campaign/campaign-map-view/CampaignMapViewComponents";
import LocationOfIntrestForm from "../../../../mantaray/components/map-view/location-of-intrest/LocationOfIntrestForm";
import LocationOfIntrestMarker from "../../../../mantaray/components/map-view/location-of-intrest/LocationOfIntrestMarker";
import HeatmapLoadingIndicator from "../../../../components/heatmap-loading-indicator/HeatmapLoadingIndicator";

// CSS
import "../../../../pages/map-view/MapView.scss";

// context
export const orgMediaContext = createContext(null);

/**
 * Page
 */
function CampaignMapView() {
  const { id, cityId } = useParams();

  // useLocation
  const location = useLocation();
  const { targetGroupId = "" } = location?.state || {};

  // State
  const [selectedTab, setSelectedTab] = useState(0);
  const [hoveredBoundaryInfo, setHoveredBoundaryInfo] = useState({});
  const [locationStr, setLocationStr] = useState("");
  const [coordinate, setCoordinate] = useState([]);
  const [isOrgMedia, setIsOrgMedia] = useState(false);

  // Selector State
  const restoredCenter = useSelector(
    (state) => state.saveMapState.restoreCenter
  );

  const restoreLoading = useSelector(
    (state) => state.saveMapState.restoreLoading
  );

  const restoredZoom =
    useSelector((state) => state.saveMapState.restoreZoom) ||
    MapView.ZOOM_INDEX;

  const bbox = useSelector((state) => state.saveMapState.cityBBox);

  // Confirmation Form :: [true/false and dataObject]
  const openConfirmationForm = useSelector(
    (state) => state.confirmationModal.openModal
  );

  const openLocationOfIntrestForm = useSelector(
    (state) => state.locationOfIntrest.openModal
  );

  const processDataObject = useSelector(
    (state) => state.confirmationModal.processDataObject
  );

  // all "poiTypeLayer" points combined array to Show on Map after selecting any TG
  const allPoiPointsArr = useSelector((state) => state.geoData.allPoiPointsArr);

  // Dispatch
  const dispatch = useDispatch();

  // Reset Map State
  useResetMapState();

  useEffect(() => {
    dispatch(restoreMapSavedState(id, cityId));
  }, [dispatch, id, cityId]);

  // default campaign custom tg heatmap generation before savedMapState
  useGetCampaignCustomTgHeatMap(bbox);

  // Functions
  function tabOnClick(index) {
    if (selectedTab === index) {
      setSelectedTab(0);
      return;
    }
    setSelectedTab(index);
  }

  // This is passed in "ConfirmAction Form" to Remove selected media-site
  function removeMediaSiteFn() {
    dispatch(removeMediaSite(id, processDataObject.campaignMediaInfo));
  }

  return (
    <orgMediaContext.Provider value={[isOrgMedia, setIsOrgMedia]}>
      <div className="content-wrapper map-content" id={CampaignMapViewId}>
        {/* Header-Sections */}
        <CampaignMapViewHeader />

        {/* Loader and MapView */}
        {restoreLoading ? (
          <MapViewLoader />
        ) : (
          <>
            {/* Tabs Section */}
            <MapTabsSection
              selectedTab={selectedTab}
              tabOnClick={tabOnClick}
              campaignId={id}
              cityId={cityId}
              bbox={bbox}
            />

            {/* Map Container */}
            {restoredCenter && (
              <div className="map-cont">
                <LLMap
                  center={[
                    Number(restoredCenter[0]),
                    Number(restoredCenter[1]),
                  ]}
                  zoom={restoredZoom}
                >
                  {/* LocationOfIntrestMarker */}
                  {coordinate.length > 0 && (
                    <LocationOfIntrestMarker position={coordinate} />
                  )}

                  {/* CampaignMedia and InventoryMedia Markers */}
                  <SiteMarkers isOrgMedia={isOrgMedia} />

                  <PoiMarkers />
                  <DataLayerMarkers cityId={cityId} />

                  {/* This component covers pinCodes and Wards Layers */}
                  <BoundaryMarkers
                    setHoveredBoundaryInfo={setHoveredBoundaryInfo}
                  />

                  <SecLayer />

                  {/* Target-Group-HeatMap */}
                  <TargetGroupHeatmap
                    hmkey={targetGroupId}
                    allPoiPointsArr={allPoiPointsArr}
                  />
                  <RoadStretchMarkers />

                  {/* Map Info Box */}
                  <MapInfoBox hoveredBoundaryInfo={hoveredBoundaryInfo} />

                  {/* Pincode Ranking */}
                  <PincodeRankingMarkers />

                  {/* Map Zoom Indicator */}
                  <MapZoomIndicator />

                  {/* Heatmap Loading Indicator */}
                  <HeatmapLoadingIndicator />
                </LLMap>
              </div>
            )}

            {/* Legends for mapView */}
            <MapControlsSection />
          </>
        )}

        {/** Modals */}
        {openConfirmationForm && (
          <ConfirmationForm onClick={removeMediaSiteFn} />
        )}

        {openLocationOfIntrestForm && (
          <LocationOfIntrestForm
            locationStr={locationStr}
            setLocationStr={setLocationStr}
            setCoordinate={setCoordinate}
            center={restoredCenter}
          />
        )}
      </div>
    </orgMediaContext.Provider>
  );
}

export default CampaignMapView;
